import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Bot, Mail, MessageSquare, Phone, MessageCircle, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { useAIState } from '../../contexts/AIStateContext';

// Derive an agent's honest operational status from what the API already returns
// (effective_status = enabled | paused | disabled), so the card never implies a
// suspended agent is in use.
const statusOf = (agent) => {
  const es = agent.effective_status
    || (agent.auto_paused ? 'paused' : (agent.enabled ? 'enabled' : 'disabled'));
  if (es === 'enabled') return { key: 'active', label: 'Active', cls: 'border-green-500/40 text-green-600 dark:text-green-400' };
  if (es === 'paused') return { key: 'paused', label: 'Paused', cls: 'border-amber-500/40 text-amber-600 dark:text-amber-400' };
  return { key: 'suspended', label: 'Suspended', cls: 'border-muted text-muted-foreground' };
};

// Per-agent communication-channel control, surfaced in the Brain Command Center.
// Self-contained: fetches agents + integration status and writes each toggle to
// POST /api/tenant/ai/agents/{id}/channels (the same endpoint AI Settings uses).
export default function AgentChannelsCard() {
  const { effectiveMode, tenantType, billingStatus } = useAIState();
  const executionMode = effectiveMode;
  const hasActiveSubscription = tenantType === 'PAID' && billingStatus === 'active';

  const [agents, setAgents] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get('/api/tenant/ai/agents'),
        api.get('/api/tenant/ai/integration-status').catch(() => ({ data: null })),
      ]);
      setAgents(a.data?.agents || []);
      setIntegrationStatus(s.data || null);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleChannel = async (agent, channel, checked) => {
    const next = { ...(agent.channels || {}), [`${channel}_enabled`]: checked };
    setSaving(`${agent.id}-${channel}`);
    // optimistic
    setAgents((prev) => prev.map((x) => (x.id === agent.id ? { ...x, channels: next } : x)));
    try {
      await api.post(`/api/tenant/ai/agents/${agent.id}/channels`, next);
      toast.success(`${agent.name}: ${channel} ${checked ? 'enabled' : 'disabled'}`);
    } catch (e) {
      // revert on failure
      setAgents((prev) => prev.map((x) => (x.id === agent.id ? { ...x, channels: agent.channels } : x)));
      toast.error(e.response?.data?.detail || 'Failed to update channel');
    } finally {
      setSaving('');
    }
  };

  if (denied) return null;

  const notLive = executionMode !== 'LIVE';
  const emailReady = integrationStatus?.integrations?.email_ready;

  const ChannelSwitch = ({ agent, channel, icon: Icon, label, disabled, hint }) => (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Switch
          checked={!!agent.channels?.[`${channel}_enabled`]}
          onCheckedChange={(c) => toggleChannel(agent, channel, c)}
          disabled={disabled || saving === `${agent.id}-${channel}`}
          data-testid={`bcc-channel-${channel}-${agent.id}`}
        />
        <Label className="flex items-center gap-1 text-sm"><Icon className="h-4 w-4" /> {label}</Label>
      </div>
      {hint && <span className="text-[11px] text-amber-600 dark:text-amber-400 ml-9">{hint}</span>}
    </div>
  );

  return (
    <Card data-testid="agent-channels-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Bot className="h-4 w-4 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">Agent Channels</CardTitle>
            <CardDescription className="text-xs">
              Control which channels each AI agent can use for outreach
              {notLive && <span className="ml-1 text-amber-600">· LIVE mode required for email/SMS/voice</span>}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : agents.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No AI agents configured yet.</p>
        ) : (
          <div className="space-y-3">
            {agents.map((agent) => {
              const st = statusOf(agent);
              const suspended = st.key === 'suspended';
              return (
              <div
                key={agent.id}
                className={cn("rounded-lg border border-border p-3", suspended && "opacity-60")}
                data-testid={`bcc-agent-${agent.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{agent.name}</span>
                    <Badge variant="outline" className={cn("text-[10px]", st.cls)} data-testid={`bcc-agent-status-${agent.id}`}>
                      {st.label}
                    </Badge>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize">{agent.type} agent</Badge>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  <ChannelSwitch agent={agent} channel="email" icon={Mail} label="Email"
                    disabled={notLive || suspended}
                    hint={suspended ? 'Agent suspended — enable it in AI Settings' : (emailReady === false ? '⚠ SMTP not configured' : '')} />
                  <ChannelSwitch agent={agent} channel="sms" icon={MessageSquare} label="SMS"
                    disabled={notLive || suspended || !hasActiveSubscription} />
                  <ChannelSwitch agent={agent} channel="voice" icon={Phone} label="Voice"
                    disabled={notLive || suspended || !hasActiveSubscription} />
                  <ChannelSwitch agent={agent} channel="chat" icon={MessageCircle} label="Chat" disabled={suspended} />
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
